import React, { useState, useEffect } from 'react';
import { Layout, Menu } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  DollarOutlined,
  CalculatorOutlined,
  BarChartOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  SafetyOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { APP_CONFIG } from '../../config';

const { Sider } = Layout;

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  path: string;
  requireSuperAdmin?: boolean;
  requireAdmin?: boolean;
}

const menuItems: MenuItem[] = [
  {
    key: 'dashboard',
    icon: <DashboardOutlined />,
    label: '仪表板',
    path: '/dashboard',
  },
  {
    key: 'calculation',
    icon: <CalculatorOutlined />,
    label: '业绩计算',
    path: '/calculation',
  },
  {
    key: 'users',
    icon: <UserOutlined />,
    label: '用户管理',
    path: '/users',
    requireAdmin: true,
  },
  {
    key: 'admins',
    icon: <SafetyOutlined />,
    label: '管理员管理',
    path: '/admins',
    requireSuperAdmin: true,
  },
  {
    key: 'referrals',
    icon: <TeamOutlined />,
    label: '推荐官管理',
    path: '/referrals',
    requireAdmin: true,
  },
  {
    key: 'profit',
    icon: <DollarOutlined />,
    label: '分润管理',
    path: '/profit',
    requireAdmin: true,
  },
  {
    key: 'verification',
    icon: <SafetyCertificateOutlined />,
    label: '确权管理',
    path: '/verification',
    requireAdmin: true,
  },
  {
    key: 'analytics',
    icon: <BarChartOutlined />,
    label: '数据分析',
    path: '/analytics',
    requireAdmin: true,
  },
  {
    key: 'settings',
    icon: <SettingOutlined />,
    label: '系统设置',
    path: '/settings',
    requireAdmin: true,
  },
  {
    key: 'logs',
    icon: <FileTextOutlined />,
    label: '操作日志',
    path: '/logs',
    requireSuperAdmin: true,
  },
];

const Sidebar: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<any>(null);

  // 获取用户信息
  useEffect(() => {
    const userStr = localStorage.getItem(APP_CONFIG.userKey);
    console.log('[Sidebar] 从localStorage读取用户信息:', userStr);
    if (userStr) {
      const parsedUser = JSON.parse(userStr);
      console.log('[Sidebar] 解析后的用户信息:', parsedUser);
      setUser(parsedUser);
    }
  }, []);

  const handleMenuClick = ({ key }: { key: string }) => {
    const item = menuItems.find(item => item.key === key);
    if (item) {
      navigate(item.path);
    }
  };

  const getSelectedKey = () => {
    const path = location.pathname;
    const item = menuItems.find(item => item.path === path);
    return item ? item.key : 'dashboard';
  };

  const toggleCollapsed = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
  };

  // 过滤菜单项（根据用户权限）
  const filteredMenuItems = menuItems.filter(item => {
    // 如果用户信息还没加载完成，显示默认菜单
    if (!user) {
      console.log('[Sidebar] 用户信息未加载，显示默认菜单');
      return item.key === 'dashboard' || item.key === 'calculation';
    }

    // 非管理员角色（retail、level1、level2、level3、regional）只能看到仪表板和业绩计算
    const nonAdminRoles = ['retail', 'level1', 'level2', 'level3', 'regional'];
    if (nonAdminRoles.includes(user.role)) {
      console.log(`[Sidebar] 非管理员用户（${user.role}），只显示仪表板和业绩计算`);
      return item.key === 'dashboard' || item.key === 'calculation';
    }

    // 管理员可以访问所有管理功能
    if (user.role === 'admin') {
      // 检查是否需要超级管理员权限
      if (item.requireSuperAdmin) {
        console.log(`[Sidebar] 检查菜单项 ${item.label} 需要超级管理员权限:`, item.requireSuperAdmin);
        console.log(`[Sidebar] 用户isSuperAdmin:`, user.isSuperAdmin);
        return user.isSuperAdmin;
      }
      return true;
    }

    // 未登录或角色未知，不显示任何菜单
    return false;
  });

  console.log('[Sidebar] 过滤后的菜单项:', filteredMenuItems.map(item => item.label));

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={200}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
      }}
    >
      <div className="logo" style={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {collapsed ? '甄来电' : '甄来电管理系统'}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        items={filteredMenuItems.map(item => ({
          key: item.key,
          icon: item.icon,
          label: item.label,
        }))}
        onClick={handleMenuClick}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 0,
          right: 0,
          textAlign: 'center',
        }}
      >
        {React.createElement(
          collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
          {
            className: 'trigger',
            onClick: toggleCollapsed,
            style: { fontSize: '18px', color: 'white', cursor: 'pointer' },
          }
        )}
      </div>
    </Sider>
  );
};

export default Sidebar;